// Client-side IV-with-nurse pathway (ZO-11165, full-pathway build).
//
// The client analog of the WA3 operator demo (IvBookingDemo.tsx), but every
// call is attributed to the logged-in CLIENT's own token — there is NO "Select
// client" step; the authenticated caller IS the patient.
//
// Flow: Select IV service -> Screening (client-attributed) -> Location ->
// Payment -> Review -> create booking. The nurse is matched SERVER-SIDE
// (booking_option = ANYONE -> ScheduledBookingMatchingService); the patient
// does not (and per Grails ROLE_USER auth, cannot) hand-pick or assign a nurse,
// nor auto-sign their own Rx. Screening is enforced by patient-screening-service:
// the booking's createSingle calls /internal/screenings/validate for the product's
// screening_service_code, so the client must self-screen here under
// request_profile_id 0 (self) for that eligibility check to find the PASS.
import React, { useCallback, useEffect, useState } from "react";
import {
  AppBar,
  Toolbar,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  MenuItem,
  CircularProgress,
  Divider,
  Chip,
  Grid,
  Card,
  CardActionArea,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  makeStyles,
} from "@material-ui/core";
import { v4 as uuidv4 } from "uuid";
import { clientLogin, isAuthenticated } from "services/authentication.service";
import { ScreeningService } from "services/screening.service";
import { getIvCatalog } from "services/service.service";
import locationsService from "services/locations.service";
import { fetchExistingPaymentMethods } from "services/payments.service";
import { createNewBooking } from "services/booking.service";

const STEPS = ["Select IV service", "Screening", "Location", "Payment", "Review", "Done"];
const SERVICE = 0;
const SCREENING = 1;
const LOCATION = 2;
const PAYMENT = 3;
const REVIEW = 4;
const DONE = 5;

const PESO = new Intl.NumberFormat("en-PH", { style: "currency", currency: "PHP" });
const peso = (n) => (typeof n === "number" && !Number.isNaN(n) ? PESO.format(n) : "—");

const useStyles = makeStyles((theme) => ({
  appBar: { backgroundColor: "#74BACD" },
  container: { marginTop: 96, marginBottom: 48 },
  stepper: { background: "transparent", padding: theme.spacing(2, 0, 3) },
  paper: { padding: theme.spacing(4), borderRadius: 16, maxWidth: 960, margin: "0 auto" },
  field: { marginBottom: theme.spacing(2) },
  actions: { marginTop: theme.spacing(3), display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" },
  card: { height: "100%", borderRadius: 12, border: "2px solid transparent" },
  cardSelected: { borderColor: theme.palette.primary.main },
  cardBody: { display: "flex", flexDirection: "column", gap: 6, height: "100%" },
  cardIcon: { width: 40, height: 40, objectFit: "contain", marginBottom: 4 },
  chipRow: { marginTop: "auto", display: "flex", gap: 8, flexWrap: "wrap", paddingTop: 8 },
  screenChip: { backgroundColor: "#fbe9a7" },
  q: { marginBottom: theme.spacing(2) },
  subtle: { color: theme.palette.text.secondary },
  summary: { margin: theme.spacing(1, 0), "& dt": { fontWeight: 600 }, "& dd": { margin: "0 0 8px" } },
  devBox: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5),
    border: "1px dashed #f0c36d",
    background: "#fff8e6",
    borderRadius: 8,
  },
  stateBox: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5, 2),
    border: "1px solid",
    borderRadius: 8,
  },
}));

// ── show_if evaluator (mirrors ClientScreening / patient-screening-web / WA3) ──
function asNumber(v) {
  return typeof v === "number" ? v : Number(v);
}
function evalFieldOp(actual, opObj) {
  return Object.entries(opObj).every(([op, expected]) => {
    switch (op) {
      case "eq": return actual === expected;
      case "neq": return actual !== expected;
      case "is_true": return actual === true;
      case "is_false": return actual === false;
      case "includes": return Array.isArray(actual) && actual.includes(expected);
      case "includes_any":
        return Array.isArray(actual) && Array.isArray(expected) && expected.some((e) => actual.includes(e));
      case "between": {
        if (!Array.isArray(expected) || actual === undefined || actual === null || actual === "") return false;
        const n = asNumber(actual);
        return !Number.isNaN(n) && n >= asNumber(expected[0]) && n <= asNumber(expected[1]);
      }
      case "gt": return !Number.isNaN(asNumber(actual)) && asNumber(actual) > asNumber(expected);
      case "gte": return !Number.isNaN(asNumber(actual)) && asNumber(actual) >= asNumber(expected);
      case "lt": return !Number.isNaN(asNumber(actual)) && asNumber(actual) < asNumber(expected);
      case "lte": return !Number.isNaN(asNumber(actual)) && asNumber(actual) <= asNumber(expected);
      default: return true;
    }
  });
}
function evalShowIf(cond, answers) {
  if (!cond || typeof cond !== "object") return true;
  if (Array.isArray(cond.all_of)) return cond.all_of.every((s) => evalShowIf(s, answers));
  if (Array.isArray(cond.any_of)) return cond.any_of.some((s) => evalShowIf(s, answers));
  if ("not" in cond) return !evalShowIf(cond.not, answers);
  return Object.entries(cond).every(([field, opObj]) =>
    opObj && typeof opObj === "object" ? evalFieldOp(answers[field], opObj) : true,
  );
}
function isVisible(q, answers) {
  return q.show_if === undefined || q.show_if === null || evalShowIf(q.show_if, answers);
}
const qLabel = (q) => q.label?.en || q.code;
const optLabel = (o) => o.label?.en || o.value;
const itemLabel = (it) => it?.label || `Item #${it?.id}`;

function deviceId() {
  let d = localStorage.getItem("screening_device_id");
  if (!d) {
    d = uuidv4();
    localStorage.setItem("screening_device_id", d);
  }
  return d;
}
function currentUser() {
  try {
    return JSON.parse(localStorage.getItem("user")) || null;
  } catch {
    return null;
  }
}
const clientIdOf = (u) => u?.client?.id || u?.user?.id;
const locationLabel = (l) =>
  `${l?.label || l?.type?.name || "Location"}${l?.address ? ` — ${l.address}` : ""}`;
const paymentLabel = (p) =>
  `${p?.card_type || p?.type || "Card"} ••••${p?.last4 || "????"}${p?.is_default ? " (default)" : ""}`;

// datetime-local default: tomorrow at noon, formatted YYYY-MM-DDTHH:mm.
function defaultSchedule() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(12, 0, 0, 0);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// ── dev-only screening auto-fill (ported from the WA3 IvBookingDemo) ─────────
// Shown ONLY on dev hosts (fail-closed — never app.zennya.com). Builds a
// type-correct answer set from the LIVE template so every question is filled,
// then nudges the outcome: under-18 → hard_block, chronic condition →
// soft_flag. The rule engine ignores codes not present in the live template.
const IS_DEV_ENV =
  typeof window !== "undefined" &&
  ["dev.app.zennya.com", "localhost", "127.0.0.1"].includes(window.location.hostname);

const BENIGN_SINGLE_SELECT_VALUES = new Set([
  "no", "none", "never", "normal", "no_normal", "negative", "na", "not_applicable",
  "single_old", "class_i_ii", "class_i",
]);
const SAFE_NUMERIC_DEFAULTS = {
  "C1.a": 30, // age
  "C1.c": 70, // weight (kg)
  "C1.d": 170, // height (cm)
  "C-bp-systolic": 120,
  "C-bp-diastolic": 80,
};
function benignAnswer(q) {
  switch (q.type) {
    case "attestation": return true;
    case "boolean": return false;
    case "multi_select":
      return (q.options ?? []).some((o) => o.value === "none") ? ["none"] : [];
    case "single_select": {
      const opts = q.options ?? [];
      const benign = opts.find((o) => BENIGN_SINGLE_SELECT_VALUES.has(o.value));
      return (benign ?? opts[0])?.value;
    }
    case "number": return SAFE_NUMERIC_DEFAULTS[q.code];
    default: return undefined; // text / file_upload → leave blank
  }
}
function buildPresetAnswers(questions, outcome) {
  const a = {};
  for (const q of questions) {
    const v = benignAnswer(q);
    if (v !== undefined) a[q.code] = v;
  }
  if (outcome === "hard_block") a["C1.a"] = 16; // under 18 → baseline hard_block
  else if (outcome === "soft_flag") a["C5.a"] = ["heart_disease"]; // chronic → soft_flag
  return a;
}

const ClientIvPathway = () => {
  const classes = useStyles();
  const [authed, setAuthed] = useState(isAuthenticated());
  const [user, setUser] = useState(currentUser());

  // login
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [activeStep, setActiveStep] = useState(SERVICE);

  // catalog
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [serviceTypeId, setServiceTypeId] = useState(null);
  const [item, setItem] = useState(null);

  // screening
  const [template, setTemplate] = useState(null);
  const [session, setSession] = useState(null);
  const [answers, setAnswers] = useState({});
  const [evaluation, setEvaluation] = useState(null);
  const [screenSkipped, setScreenSkipped] = useState(false);
  const [screeningState, setScreeningState] = useState(null); // prior review-aware state (GET /screening/state)
  const [usedExisting, setUsedExisting] = useState(false); // chose "Continue with this screening"

  // booking inputs
  const [locations, setLocations] = useState([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [location, setLocation] = useState(null);
  const [payments, setPayments] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [payment, setPayment] = useState(null);
  const [dateScheduled, setDateScheduled] = useState(defaultSchedule());

  // booking result
  const [booking, setBooking] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const errMsg = (e, fallback) =>
    e?.body?.detail || e?.response?.data?.message?.en || e?.response?.data?.message || e?.message || fallback;
  const eligible = screenSkipped || usedExisting || evaluation?.outcome === "pass";

  // ── auth ──────────────────────────────────────────────────────────────────
  const handleLogin = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await clientLogin({ email: email.trim(), password, fingerprint: deviceId() });
      setUser(currentUser());
      setAuthed(true);
    } catch (e) {
      setError(errMsg(e, "Login failed — check email / password."));
    } finally {
      setLoading(false);
    }
  }, [email, password]);

  const handleLogout = useCallback(() => {
    localStorage.removeItem("user");
    setAuthed(false);
    setUser(null);
  }, []);

  // ── catalog ─────────────────────────────────────────────────────────────
  const loadCatalog = useCallback(async () => {
    setCatalogLoading(true);
    setError(null);
    try {
      const { items: list, serviceTypeId: stid } = await getIvCatalog();
      setItems(list);
      setServiceTypeId(stid);
    } catch (e) {
      setError(errMsg(e, "Failed to load the IV catalog."));
    } finally {
      setCatalogLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authed && activeStep === SERVICE && items.length === 0 && !catalogLoading) loadCatalog();
  }, [authed, activeStep, items.length, catalogLoading, loadCatalog]);

  const selectItem = useCallback(
    (it) => {
      if (item?.id === it.id) return;
      setItem(it);
      setTemplate(null);
      setSession(null);
      setAnswers({});
      setEvaluation(null);
      setScreenSkipped(false);
      setScreeningState(null);
      setUsedExisting(false);
    },
    [item],
  );

  const handleServiceContinue = useCallback(async () => {
    if (!item) return;
    const code = item.screening_service_code;
    if (!code) {
      setTemplate(null);
      setSession(null);
      setEvaluation(null);
      setScreenSkipped(true);
      setScreeningState(null);
      setUsedExisting(false);
      setActiveStep(LOCATION);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const tpl = await ScreeningService.resolveTemplate(code);
      if (!tpl) {
        setTemplate(null);
        setSession(null);
        setScreenSkipped(true);
        setScreeningState(null);
        setUsedExisting(false);
        setActiveStep(LOCATION);
        return;
      }
      const newSession = await ScreeningService.createSession({
        template_key: tpl.template_key,
        service_code: code,
        source_app: "web",
        request_profile_id_override: 0, // self booking -> profile 0, so /state & booking /validate find it
      });
      // Best-effort: read any prior review-aware screening on file for this
      // client + service (request_profile 0 = self), so the screening step can
      // offer to reuse an eligible result instead of forcing a re-screen.
      // Mirrors IvBookingDemo's getCurrentState; the questionnaire still works
      // if this read fails.
      let priorState = null;
      try {
        priorState = await ScreeningService.getState(code, 0);
      } catch {
        priorState = null;
      }
      setTemplate(tpl);
      setSession(newSession);
      setAnswers({});
      setEvaluation(null);
      setScreenSkipped(false);
      setScreeningState(priorState);
      setUsedExisting(false);
      setActiveStep(SCREENING);
    } catch (e) {
      setError(errMsg(e, "Couldn't start screening for this service."));
    } finally {
      setLoading(false);
    }
  }, [item]);

  // ── screening ─────────────────────────────────────────────────────────────
  const setAnswer = useCallback((code, value) => {
    setAnswers((prev) => ({ ...prev, [code]: value }));
  }, []);

  // Dev-only: replace all answers with a preset that drives a target outcome.
  const applyPreset = useCallback(
    (outcome) => setAnswers(buildPresetAnswers(template?.questions ?? [], outcome)),
    [template],
  );

  const handleEvaluate = useCallback(async () => {
    if (!template) return;
    setLoading(true);
    setError(null);
    try {
      const byCode = new Map((template.questions ?? []).map((q) => [q.code, q]));
      const payload = {};
      Object.entries(answers).forEach(([code, val]) => {
        if (val === undefined || val === null || val === "") return;
        const q = byCode.get(code);
        if (q && !isVisible(q, answers)) return; // strip hidden show_if answers
        payload[code] = val;
      });
      // A sealed evaluation is never mutated — re-evaluating spins a new session.
      let sess = session;
      if (!sess || evaluation) {
        sess = await ScreeningService.createSession({
          template_key: template.template_key,
          service_code: item.screening_service_code,
          source_app: "web",
          request_profile_id_override: 0, // self booking -> profile 0 (see handleServiceContinue)
        });
        setSession(sess);
      }
      await ScreeningService.upsertAnswers(sess.id, payload);
      const ev = await ScreeningService.evaluate(sess.id);
      setEvaluation(ev);
      setUsedExisting(false); // a fresh evaluation supersedes any "use existing" choice
    } catch (e) {
      setError(errMsg(e, "Evaluation failed."));
    } finally {
      setLoading(false);
    }
  }, [template, answers, session, evaluation, item]);

  // Reuse an eligible screening already on file (from getState) — skip the
  // questionnaire and continue straight to Location, mirroring IvBookingDemo's
  // "Continue with this screening". No getEvaluation needed: the booking's
  // /internal/screenings/validate re-checks eligibility under profile 0.
  const handleUseExisting = useCallback(() => {
    setUsedExisting(true);
    setActiveStep(LOCATION);
  }, []);

  // ── location + payment ────────────────────────────────────────────────────
  const loadLocations = useCallback(async () => {
    setLocationsLoading(true);
    setError(null);
    try {
      const list = (await locationsService.getUserLocations(clientIdOf(user), { confirmedOnly: false })) || [];
      setLocations(list);
    } catch (e) {
      setError(errMsg(e, "Failed to load your saved locations."));
    } finally {
      setLocationsLoading(false);
    }
  }, [user]);

  const loadPayments = useCallback(async () => {
    setPaymentsLoading(true);
    setError(null);
    try {
      const data = await fetchExistingPaymentMethods();
      setPayments(data?.list ?? (Array.isArray(data) ? data : []));
    } catch (e) {
      setError(errMsg(e, "Failed to load your payment methods."));
    } finally {
      setPaymentsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authed && activeStep === LOCATION && locations.length === 0 && !locationsLoading) loadLocations();
  }, [authed, activeStep, locations.length, locationsLoading, loadLocations]);
  useEffect(() => {
    if (authed && activeStep === PAYMENT && payments.length === 0 && !paymentsLoading) loadPayments();
  }, [authed, activeStep, payments.length, paymentsLoading, loadPayments]);

  // ── create booking ──────────────────────────────────────────────────────
  const handleCreateBooking = useCallback(async () => {
    if (!item || !location || !payment || !serviceTypeId) return;
    setLoading(true);
    setError(null);
    try {
      const clientId = clientIdOf(user);
      const params = {
        lat: location.latitude,
        lng: location.longitude,
        location_id: location.id,
        address: location.address,
        payment_method_token: payment.token,
        should_check_for_payment: true,
        client_type: "web",
        // IV screening is done in patient-screening-service and gated CLIENT-SIDE
        // (only a PASS reaches this step). Grails' should_check_for_screening /
        // _medical_id are the legacy answer-based gates and do NOT recognize a
        // patient-screening-service eval — they'd 403 a genuinely-passed patient.
        // Omitted here, same as the WA3 IvBookingDemo's createScheduledBooking.
        booking_option: 2, // ANYONE -> server-side nurse matching
        type: serviceTypeId,
        extended_package_items: [item.id],
        extended_packages: [],
        gender: "any",
        require_location_confirmation: false,
        service_addon_order: [{ type: "service", service_addon_id: serviceTypeId, priority: 1 }],
        duration: 60,
        date_scheduled: new Date(dateScheduled).toISOString(),
        should_check_high_availability: true,
      };
      const res = await createNewBooking(clientId, params);
      setBooking(res);
      setActiveStep(DONE);
    } catch (e) {
      setError(errMsg(e, "Couldn't create the booking."));
    } finally {
      setLoading(false);
    }
  }, [item, location, payment, serviceTypeId, user, dateScheduled]);

  const handleStartOver = useCallback(() => {
    setItem(null);
    setTemplate(null);
    setSession(null);
    setAnswers({});
    setEvaluation(null);
    setScreenSkipped(false);
    setScreeningState(null);
    setUsedExisting(false);
    setLocation(null);
    setPayment(null);
    setBooking(null);
    setError(null);
    setActiveStep(SERVICE);
  }, []);

  const renderQuestion = (q) => {
    const value = answers[q.code];
    if (q.type === "attestation") {
      return (
        <FormControlLabel
          key={q.code}
          className={classes.q}
          control={<Checkbox checked={value === true} onChange={(e) => setAnswer(q.code, e.target.checked)} />}
          label={qLabel(q)}
        />
      );
    }
    if (q.type === "boolean") {
      return (
        <FormControl key={q.code} component="fieldset" className={classes.q} fullWidth>
          <FormLabel component="legend">{qLabel(q)}</FormLabel>
          <RadioGroup
            row
            value={value === undefined || value === null ? "" : String(value)}
            onChange={(e) => setAnswer(q.code, e.target.value === "true")}
          >
            <FormControlLabel value="true" control={<Radio />} label="Yes" />
            <FormControlLabel value="false" control={<Radio />} label="No" />
          </RadioGroup>
        </FormControl>
      );
    }
    if (q.type === "single_select" && q.options) {
      return (
        <TextField
          key={q.code}
          select
          fullWidth
          variant="outlined"
          className={classes.q}
          label={qLabel(q)}
          value={value === undefined || value === null ? "" : String(value)}
          onChange={(e) => setAnswer(q.code, e.target.value)}
        >
          {q.options.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {optLabel(o)}
            </MenuItem>
          ))}
        </TextField>
      );
    }
    if (q.type === "multi_select" && q.options) {
      const selected = Array.isArray(value) ? value : [];
      const toggle = (v) =>
        setAnswer(q.code, selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
      return (
        <FormControl key={q.code} component="fieldset" className={classes.q} fullWidth>
          <FormLabel component="legend">{qLabel(q)}</FormLabel>
          {q.options.map((o) => (
            <FormControlLabel
              key={o.value}
              control={<Checkbox checked={selected.includes(o.value)} onChange={() => toggle(o.value)} />}
              label={optLabel(o)}
            />
          ))}
        </FormControl>
      );
    }
    if (q.type === "number") {
      return (
        <TextField
          key={q.code}
          type="number"
          fullWidth
          variant="outlined"
          className={classes.q}
          label={qLabel(q)}
          value={value === undefined || value === null ? "" : String(value)}
          onChange={(e) => setAnswer(q.code, e.target.value === "" ? undefined : Number(e.target.value))}
        />
      );
    }
    return (
      <TextField
        key={q.code}
        fullWidth
        variant="outlined"
        className={classes.q}
        label={qLabel(q)}
        value={value === undefined || value === null ? "" : String(value)}
        onChange={(e) => setAnswer(q.code, e.target.value)}
      />
    );
  };

  const visibleQuestions = (template?.questions ?? [])
    .filter((q) => isVisible(q, answers))
    .sort((a, b) => (a.sort_order ?? Number.MAX_SAFE_INTEGER) - (b.sort_order ?? Number.MAX_SAFE_INTEGER));

  // ── step renderers ────────────────────────────────────────────────────────
  const renderServiceStep = () => (
    <>
      <Typography variant="h6" gutterBottom>Select IV service</Typography>
      <Typography variant="body2" className={classes.subtle} gutterBottom>
        Drips are loaded live from the IV-therapy catalog. Each card shows whether a patient
        screening is required for that product.
      </Typography>
      {catalogLoading ? (
        <div className={classes.actions}><CircularProgress size={22} /> <Typography variant="body2">Loading catalog…</Typography></div>
      ) : items.length === 0 ? (
        <div className={classes.actions}>
          <Typography variant="body2">No catalog items returned.</Typography>
          <Button variant="outlined" onClick={loadCatalog}>Retry</Button>
        </div>
      ) : (
        <Grid container spacing={2} style={{ marginTop: 8 }}>
          {items.map((it) => {
            const selected = item?.id === it.id;
            return (
              <Grid item xs={12} sm={6} md={4} key={it.id}>
                <Card className={`${classes.card} ${selected ? classes.cardSelected : ""}`} variant="outlined">
                  <CardActionArea onClick={() => selectItem(it)} style={{ height: "100%" }}>
                    <CardContent className={classes.cardBody}>
                      {it.icon_url && <img src={it.icon_url} alt="" className={classes.cardIcon} />}
                      <Typography variant="subtitle1"><strong>{itemLabel(it)}</strong></Typography>
                      {it.description && (
                        <Typography variant="body2" className={classes.subtle}>{it.description}</Typography>
                      )}
                      <div className={classes.chipRow}>
                        <Chip size="small" label={peso(it.fee)} />
                        {it.screening_service_code ? (
                          <Chip size="small" className={classes.screenChip} label={`Screening: ${it.screening_service_code}`} />
                        ) : (
                          <Chip size="small" variant="outlined" label="No screening required" />
                        )}
                      </div>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
      <div className={classes.actions}>
        <Button variant="contained" color="primary" disabled={loading || !item} onClick={handleServiceContinue}>
          {loading ? "Working…" : "Continue"}
        </Button>
      </div>
    </>
  );

  const renderExistingScreening = () => {
    const st = screeningState;
    if (!st) return null;
    const code = item?.screening_service_code;
    if (!st.found) {
      return (
        <div className={classes.stateBox} style={{ borderColor: "#90caf9", background: "#e3f2fd" }}>
          <Typography variant="body2">
            No prior screening on file for <code>{code}</code>. Complete a new screening below.
          </Typography>
        </div>
      );
    }
    const palette = st.eligible
      ? { border: "#a5d6a7", bg: "#e8f5e9" }
      : st.outcome === "hard_block"
        ? { border: "#ef9a9a", bg: "#ffebee" }
        : { border: "#ffcc80", bg: "#fff3e0" };
    return (
      <div className={classes.stateBox} style={{ borderColor: palette.border, background: palette.bg }}>
        <Typography variant="subtitle2" gutterBottom>Current screening — already on file</Typography>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
          <Chip size="small" label={(st.outcome || "unknown").toUpperCase()} />
          <Chip
            size="small"
            color={st.eligible ? "primary" : "default"}
            label={st.eligible ? "ELIGIBLE" : "NOT ELIGIBLE"}
          />
          {st.review_required && (
            <Chip size="small" className={classes.screenChip} label={`REVIEW: ${st.review_status || "pending"}`} />
          )}
        </div>
        <Typography variant="caption" className={classes.subtle} style={{ display: "block" }}>
          Evaluation <code>{st.evaluation_id}</code> · service <code>{code}</code>
          {st.rx_window_expires_at ? ` · Rx window until ${st.rx_window_expires_at}` : ""}
        </Typography>
        {st.eligible && (
          <div className={classes.actions}>
            <Button variant="contained" color="primary" size="small" disabled={loading} onClick={handleUseExisting}>
              Continue with this screening
            </Button>
          </div>
        )}
      </div>
    );
  };

  const renderScreeningStep = () => (
    <>
      <Typography variant="h6" gutterBottom>Screening — {itemLabel(item)}</Typography>
      {!evaluation && renderExistingScreening()}
      {!evaluation ? (
        template && session ? (
          <>
            <Typography variant="body2" className={classes.subtle} gutterBottom>
              Template <code>{template.template_key}</code> v{template.version} · session <code>{session.id}</code>
            </Typography>
            {IS_DEV_ENV && (
              <div className={classes.devBox}>
                <Typography variant="caption" className={classes.subtle} style={{ display: "block", marginBottom: 8 }}>
                  Dev auto-fill — populates every visible question; the outcome follows the live rules.
                </Typography>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <Button size="small" variant="outlined" disabled={loading} onClick={() => applyPreset("pass")}>Low-risk</Button>
                  <Button size="small" variant="outlined" disabled={loading} onClick={() => applyPreset("soft_flag")}>Soft-flag (chronic condition)</Button>
                  <Button size="small" variant="outlined" disabled={loading} onClick={() => applyPreset("hard_block")}>Hard-block (under 18)</Button>
                </div>
              </div>
            )}
            {visibleQuestions.map(renderQuestion)}
            {visibleQuestions.length === 0 && (
              <Typography variant="body2" className={classes.subtle}>Template returned no questions — you can still evaluate.</Typography>
            )}
            <div className={classes.actions}>
              <Button variant="outlined" onClick={() => setActiveStep(SERVICE)} disabled={loading}>Back</Button>
              <Button variant="contained" color="primary" disabled={loading} onClick={handleEvaluate}>
                {loading ? "Evaluating…" : "Evaluate screening"}
              </Button>
            </div>
          </>
        ) : (
          <Typography variant="body2" className={classes.subtle}>No active screening session.</Typography>
        )
      ) : (
        <>
          <Chip
            label={(evaluation.outcome || "unknown").toUpperCase()}
            color={evaluation.outcome === "pass" ? "primary" : "default"}
            style={{ marginBottom: 12 }}
          />
          <Typography variant="caption" className={classes.subtle} style={{ display: "block" }}>
            Evaluation <code>{evaluation.id}</code> · user <code>{evaluation.zennya_user_id}</code>
          </Typography>
          {(evaluation.outcome_reasons || []).length > 0 && (
            <ul>{evaluation.outcome_reasons.map((r) => <li key={r}><Typography variant="body2">{r}</Typography></li>)}</ul>
          )}
          {evaluation.outcome === "pass" && (
            <Typography variant="body2" style={{ color: "#2e7d32", marginTop: 8 }}>✓ Eligible — continue to book.</Typography>
          )}
          {evaluation.outcome === "hard_block" && (
            <Typography variant="body2" style={{ color: "#c62828", marginTop: 8 }}>
              Booking is stopped before payment for this service.
            </Typography>
          )}
          {evaluation.outcome === "soft_flag" && (
            <Typography variant="body2" style={{ color: "#ef6c00", marginTop: 8 }}>
              Routed to async doctor review — booking is held pending physician approval.
            </Typography>
          )}
          <div className={classes.actions}>
            <Button variant="outlined" onClick={() => { setEvaluation(null); }} disabled={loading}>Re-answer</Button>
            {evaluation.outcome === "pass" && (
              <Button variant="contained" color="primary" onClick={() => setActiveStep(LOCATION)} disabled={loading}>
                Continue to location
              </Button>
            )}
          </div>
        </>
      )}
    </>
  );

  const renderLocationStep = () => (
    <>
      <Typography variant="h6" gutterBottom>Where should the nurse visit?</Typography>
      {locationsLoading ? (
        <div className={classes.actions}><CircularProgress size={22} /> <Typography variant="body2">Loading locations…</Typography></div>
      ) : locations.length === 0 ? (
        <Typography variant="body2" className={classes.subtle}>
          No saved locations on your account — add one in the app first.
        </Typography>
      ) : (
        <TextField
          select fullWidth variant="outlined" className={classes.field} label="Location"
          value={location ? String(location.id) : ""}
          onChange={(e) => setLocation(locations.find((l) => String(l.id) === e.target.value) || null)}
        >
          {locations.map((l) => <MenuItem key={l.id} value={String(l.id)}>{locationLabel(l)}</MenuItem>)}
        </TextField>
      )}
      <div className={classes.actions}>
        <Button variant="outlined" onClick={() => setActiveStep(screenSkipped ? SERVICE : SCREENING)} disabled={loading}>Back</Button>
        <Button variant="contained" color="primary" disabled={loading || !location} onClick={() => setActiveStep(PAYMENT)}>Continue</Button>
      </div>
    </>
  );

  const renderPaymentStep = () => (
    <>
      <Typography variant="h6" gutterBottom>Payment method</Typography>
      <Typography variant="body2" className={classes.subtle} gutterBottom>
        Amount: {peso(item?.fee)}. The card hold is authorized server-side when the booking is created.
      </Typography>
      {paymentsLoading ? (
        <div className={classes.actions}><CircularProgress size={22} /> <Typography variant="body2">Loading payment methods…</Typography></div>
      ) : payments.length === 0 ? (
        <Typography variant="body2" className={classes.subtle}>No saved payment methods — add one in the app first.</Typography>
      ) : (
        <TextField
          select fullWidth variant="outlined" className={classes.field} label="Payment method"
          value={payment ? payment.token : ""}
          onChange={(e) => setPayment(payments.find((p) => p.token === e.target.value) || null)}
        >
          {payments.map((p) => <MenuItem key={p.token} value={p.token}>{paymentLabel(p)}</MenuItem>)}
        </TextField>
      )}
      <div className={classes.actions}>
        <Button variant="outlined" onClick={() => setActiveStep(LOCATION)} disabled={loading}>Back</Button>
        <Button variant="contained" color="primary" disabled={loading || !payment} onClick={() => setActiveStep(REVIEW)}>Continue</Button>
      </div>
    </>
  );

  const renderReviewStep = () => (
    <>
      <Typography variant="h6" gutterBottom>Review &amp; confirm</Typography>
      <dl className={classes.summary}>
        <dt>IV service</dt><dd>{itemLabel(item)} — {peso(item?.fee)}</dd>
        <dt>Screening</dt><dd>{screenSkipped ? "Not required" : usedExisting ? `${(screeningState?.outcome || "—").toUpperCase()} — existing (eval ${screeningState?.evaluation_id ?? "—"})` : `${(evaluation?.outcome || "—").toUpperCase()} (eval ${evaluation?.id ?? "—"})`}</dd>
        <dt>Location</dt><dd>{location ? locationLabel(location) : "—"}</dd>
        <dt>Payment</dt><dd>{payment ? paymentLabel(payment) : "—"}</dd>
      </dl>
      <TextField
        type="datetime-local" fullWidth variant="outlined" className={classes.field}
        label="Scheduled time" InputLabelProps={{ shrink: true }}
        value={dateScheduled} onChange={(e) => setDateScheduled(e.target.value)}
      />
      <Typography variant="caption" className={classes.subtle} style={{ display: "block" }}>
        A nurse is matched automatically after booking (no manual nurse selection).
      </Typography>
      <div className={classes.actions}>
        <Button variant="outlined" onClick={() => setActiveStep(PAYMENT)} disabled={loading}>Back</Button>
        <Button
          variant="contained" color="primary"
          disabled={loading || !item || !location || !payment || !serviceTypeId || !dateScheduled}
          onClick={handleCreateBooking}
        >
          {loading ? "Creating booking…" : "Create booking"}
        </Button>
      </div>
    </>
  );

  const renderDoneStep = () => (
    <>
      <Typography variant="h6" gutterBottom>Booking created 🎉</Typography>
      <Typography variant="body2">
        Booking <code>{booking?.id ?? "—"}</code> for <strong>{itemLabel(item)}</strong> is in. A nurse is being
        matched automatically — you'll see them in your bookings once assigned.
      </Typography>
      <div className={classes.actions}>
        <Button variant="contained" color="primary" onClick={handleStartOver}>Book another</Button>
      </div>
    </>
  );

  return (
    <div>
      <AppBar position="fixed" className={classes.appBar}>
        <Toolbar>
          <Typography variant="h6" style={{ flexGrow: 1 }}>IV self-service (client)</Typography>
          {authed && (
            <>
              <Typography variant="body2" style={{ marginRight: 16 }}>
                {user?.client?.email || user?.user?.email || `client #${clientIdOf(user)}`}
              </Typography>
              <Button color="inherit" onClick={handleLogout}>Log out</Button>
            </>
          )}
        </Toolbar>
      </AppBar>

      <Container className={classes.container}>
        {!authed ? (
          <Paper className={classes.paper} elevation={2}>
            <Typography variant="h6" gutterBottom>Log in as a client</Typography>
            <Typography variant="body2" className={classes.subtle} style={{ marginBottom: 16 }}>
              Everything you do here is attributed to this client account (the authenticated caller),
              mirroring a real patient self-booking.
            </Typography>
            <TextField fullWidth variant="outlined" className={classes.field} label="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
            <TextField fullWidth variant="outlined" type="password" className={classes.field} label="Password" value={password} onChange={(e) => setPassword(e.target.value)} />
            <div className={classes.actions}>
              <Button variant="contained" color="primary" disabled={loading || !email || !password} onClick={handleLogin}>
                {loading ? "Signing in…" : "Log in"}
              </Button>
            </div>
            {error && <Typography variant="body2" style={{ color: "#c62828", marginTop: 16 }}>{error}</Typography>}
          </Paper>
        ) : (
          <>
            <Stepper activeStep={activeStep} alternativeLabel className={classes.stepper}>
              {STEPS.map((label) => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}
            </Stepper>
            <Paper className={classes.paper} elevation={2}>
              <Typography variant="body2" className={classes.subtle} gutterBottom>
                Logged in as <strong>{user?.client?.email || user?.user?.email || `client #${clientIdOf(user)}`}</strong>
              </Typography>
              <Divider style={{ margin: "12px 0 20px" }} />
              {activeStep === SERVICE && renderServiceStep()}
              {activeStep === SCREENING && renderScreeningStep()}
              {activeStep === LOCATION && renderLocationStep()}
              {activeStep === PAYMENT && renderPaymentStep()}
              {activeStep === REVIEW && renderReviewStep()}
              {activeStep === DONE && renderDoneStep()}
              {error && <Typography variant="body2" style={{ color: "#c62828", marginTop: 16 }}>{error}</Typography>}
              {loading && <CircularProgress size={20} style={{ marginTop: 16 }} />}
            </Paper>
          </>
        )}
      </Container>
    </div>
  );
};

export default ClientIvPathway;
